/**
 * imagen.js — achicar una foto antes de subirla.
 *
 * La cámara de un celular de hoy saca fotos de 3 a 6 MB. Subirlas enteras es
 * un problema por dos lados: el servidor web corta la subida (413, y el error
 * que vuelve no es JSON, así que ni se puede explicar bien), y el operador que
 * anda en la ronda está con datos móviles, donde 5 MB por casa se sienten.
 *
 * Para lo que se usan estas fotos (leer los dígitos de un medidor, dejar
 * constancia de en qué estado estaba) no hace falta nada de esa resolución:
 * 1600 px por el lado largo deja los números perfectamente legibles y el
 * archivo queda entre 200 y 400 KB.
 *
 * Se achica ACÁ y no en el servidor a propósito: lo que no viaja no se puede
 * cortar a mitad de camino ni hace esperar a nadie.
 */

const LADO_MAX = 1600;
const CALIDAD  = 0.82;

/** Dibuja el `source` en un canvas del tamaño pedido y devuelve el blob JPEG. */
function aJpeg(source, ancho, alto, calidad) {
  const canvas = document.createElement('canvas');
  canvas.width  = ancho;
  canvas.height = alto;
  const ctx = canvas.getContext('2d');
  // Fondo blanco: un PNG con transparencia se vería negro al pasar a JPEG.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, ancho, alto);
  ctx.drawImage(source, 0, 0, ancho, alto);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', calidad));
}

/** Carga el archivo como algo que se pueda dibujar, respetando la orientación. */
async function cargar(archivo) {
  // createImageBitmap con `from-image` aplica la rotación que la cámara dejó
  // anotada en el EXIF. Sin eso, una foto tomada en vertical se sube acostada.
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(archivo, { imageOrientation: 'from-image' });
    } catch {
      /* Safari viejo no acepta las opciones: se sigue con <img>. */
    }
  }
  const url = URL.createObjectURL(archivo);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload  = () => resolve(img);
      img.onerror = () => reject(new Error('No se pudo leer la imagen'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Devuelve una versión liviana de la foto, o el archivo original si no se pudo
 * procesar (un HEIC de iPhone que el navegador no sabe decodificar, por
 * ejemplo). Nunca lanza: si algo sale mal, que se suba la foto original es
 * mejor que quedarse sin foto.
 *
 * @param {File} archivo
 * @param {{ladoMax?: number, calidad?: number}} opciones
 * @returns {Promise<File>}
 */
export async function comprimirImagen(archivo, opciones = {}) {
  const { ladoMax = LADO_MAX, calidad = CALIDAD } = opciones;
  if (!archivo || !archivo.type?.startsWith('image/')) return archivo;

  try {
    const img = await cargar(archivo);
    const w = img.width;
    const h = img.height;
    if (!w || !h) return archivo;

    const escala = Math.min(1, ladoMax / Math.max(w, h));
    const blob = await aJpeg(img, Math.round(w * escala), Math.round(h * escala), calidad);
    if (img.close) img.close();           // liberar el ImageBitmap
    if (!blob) return archivo;

    // Una imagen ya chica puede salir MÁS pesada al recodificarla (sobre todo
    // un PNG de pocos colores). En ese caso no se gana nada: se deja la de él.
    if (blob.size >= archivo.size) return archivo;

    const nombre = (archivo.name || 'foto').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nombre, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return archivo;
  }
}

/** "2.4 MB" / "320 KB" — para decirle a la persona cuánto pesa lo que subió. */
export function pesoLegible(bytes) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
